import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import map from 'lodash/map';
import noop from 'lodash/noop';

import { useAccessToken, useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage, UnwrapArray } from '@sbv-clients/util-misc';

import {
  Api,
  BaseCensusDto,
  SchedulerUnitCacheKeys,
  UnitApiUnitGetRequest,
  useApi,
} from '../index';

export const useUnitsGet = (unitApiUnitGetRequest: UnitApiUnitGetRequest) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  return useQuery({
    queryKey: [SchedulerUnitCacheKeys.Units, unitApiUnitGetRequest],
    queryFn: () =>
      api.unitApi
        .unitGet(unitApiUnitGetRequest)
        .then((response) => response?.data?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled:
      !isMultiFacility ||
      (isMultiFacility && Boolean(unitApiUnitGetRequest?.facilityId)),
    refetchOnMount: false,
  });
};
export type UnitResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useUnitsGet>['data']>
>;

export const useUnitOptionsGet = (
  unitApiUnitGetRequest: UnitApiUnitGetRequest
) => {
  const {
    data: units = [],
    isLoading,
    isSuccess,
  } = useUnitsGet(unitApiUnitGetRequest);
  const options = useMemo(
    () =>
      map(units, (item) => ({
        id: String(item.unitId),
        label: item.name,
        value: item.unitId,
      })),
    [units]
  );
  return { options, isLoading, isSuccess };
};

const updateUnit = (api: Api) => (body: BaseCensusDto & { unitId: number }) => {
  const { unitId, ...baseCensusDto } = body;
  return api.unitApi.unitIdPut({
    id: unitId,
    baseCensusDto,
  });
};
export const useUpdateUnit = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(updateUnit(api), {
    onSuccess: async () => {
      queryClient.invalidateQueries([SchedulerUnitCacheKeys.Units]).catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
export const useUnitsByShiftTypeAndPositionGet = (
  employeeId: number,
  positionId: number,
  shiftTypeId: number
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      SchedulerUnitCacheKeys.UnitsByShiftTypeAndPosition,
      {
        employeeId,
        shiftTypeId,
        positionId,
      },
    ],
    queryFn: () =>
      api.unitApi.unitUnitsByShiftTypeAndPositionGet({
        employeeId,
        shiftTypeId,
        positionId,
      }),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(shiftTypeId) && Boolean(positionId),
  });
};
